import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import 'react-json-view-lite/dist/index.css';
import { Alley } from '../../../services/entities/impl/blfApi/schemas/alley';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { Collection } from '../../../services/entities/impl/blfApi/schemas/collection';
import { Customer } from '../../../services/entities/impl/blfApi/schemas/customer';
import { Ranch } from '../../../services/entities/impl/blfApi/schemas/ranch';
import { Scan } from '../../../services/entities/impl/blfApi/schemas/scan';
import { Upload } from '../../../services/entities/impl/blfApi/schemas/upload';
import {
  UploadDetail,
  UploadSummary,
} from '../../../services/entities/impl/blfApi/schemas/uploadSummary';
import { User } from '../../../services/entities/impl/blfApi/schemas/user';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { BlockPointData } from '../../blocks/BlockPointsMap/hooks/useGetBlockPoints';
import CopyButton from '../../commonUI/CopyButton';
import { ScanData } from '../../scanDataApi/scanDataDefinitions';
import { ValidationCounts } from '../utils/validation/createValidationSummary';
import { ValidationResult } from '../utils/validation/validateSchema';
import { DebugInfoSlot } from './DebugInfoSlot';
import { JsonItemWithValidation } from './JsonItemWithValidation';
import { ValidationSummary } from './ValidationSummary';
type InputType =
  | Customer
  | Ranch
  | Block
  | User
  | Scan
  | Scan[]
  | PointDetection[]
  | ScanData[]
  | Collection
  | Alley[]
  | Upload[]
  | UploadSummary
  | UploadDetail
  | BlockPointData
  | BlockPointData[]
  | undefined
  | null;

// Same as InputType, but with ScanData[] replaced with ScanData
export type JSONInputTypes =
  | Customer
  | Ranch
  | Block
  | User
  | Scan
  | PointDetection[]
  | ScanData
  | Collection
  | Alley
  | Upload
  | UploadSummary
  | UploadDetail
  | undefined
  | null;

export interface JsonLabel {
  id: string;
  label: string;
}

type DebugInfoSectionProps = {
  sectionLabel: string;
  name?: string;
  id?: string;
  isLoading: boolean;
  jsonObjectOrArrayOfObjects: InputType;
  // If jsonLabels is a string, it will be used as a label for all items
  jsonLabels?: JsonLabel[] | string | undefined;
  showID?: boolean;
  actionButtonOrElement?: JSX.Element | null;
  // Validation props
  validationSummary?: ValidationResult;
  validationCounts?: ValidationCounts;
  itemValidations?: ValidationResult | Record<string, ValidationResult>;
  // Accordion options
  useAccordionForLargeLists?: boolean;
  accordionThreshold?: number;
  isError?: boolean;
};

const DebugInfoSection = ({
  sectionLabel,
  name = '',
  id = '',
  isLoading,
  jsonObjectOrArrayOfObjects,
  jsonLabels = undefined,
  showID = true,
  actionButtonOrElement = null,
  validationSummary,
  validationCounts,
  itemValidations = {},
  useAccordionForLargeLists = true,
  accordionThreshold = 10,
  isError = false,
}: DebugInfoSectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Determine if we should use accordion mode based on item count
  const useAccordion = useMemo(() => {
    if (!useAccordionForLargeLists) return false;
    if (!jsonObjectOrArrayOfObjects) return false;
    if (!Array.isArray(jsonObjectOrArrayOfObjects)) return false;
    return jsonObjectOrArrayOfObjects.length > accordionThreshold;
  }, [
    jsonObjectOrArrayOfObjects,
    useAccordionForLargeLists,
    accordionThreshold,
  ]);

  // State for accordions
  const [expanded, setExpanded] = useState(!useAccordion);

  // Close the accordion if the data changes
  useEffect(() => {
    setExpanded(false);
  }, [jsonObjectOrArrayOfObjects]);

  // Render content with proper validation grouping
  const renderContent = () => {
    if (isError) {
      return (
        <Alert severity='error' sx={{ mt: 1 }}>
          {t('Error loading data')}
        </Alert>
      );
    }

    if (
      !jsonObjectOrArrayOfObjects ||
      (Array.isArray(jsonObjectOrArrayOfObjects) &&
        jsonObjectOrArrayOfObjects.length === 0)
    ) {
      return (
        <Typography sx={{ color: theme.vars.palette.text.secondary, my: 2 }}>
          {t('None found')}
        </Typography>
      );
    }

    if (Array.isArray(jsonObjectOrArrayOfObjects)) {
      // For arrays, we expect itemValidations to be a Record
      const validationsMap =
        typeof itemValidations === 'object' && !('messages' in itemValidations)
          ? (itemValidations as Record<string, ValidationResult>)
          : {};

      return (
        <Box>
          {useAccordion ? (
            <Accordion
              expanded={expanded}
              onChange={() => setExpanded(!expanded)}
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '&:before': { display: 'none' }, // Remove default divider
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 1,
                  color: theme.vars.palette.text.secondary,
                  minHeight: 'auto',
                }}
              >
                <Typography>
                  {expanded ? 'Hide' : 'Show'}{' '}
                  {jsonObjectOrArrayOfObjects.length} items
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {jsonObjectOrArrayOfObjects.map((item: any, index) => (
                  <JsonItemWithValidation
                    key={index}
                    item={item}
                    validation={item.id ? validationsMap[item.id] : undefined}
                    jsonLabels={jsonLabels}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          ) : (
            // Render normally if not using accordion
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            jsonObjectOrArrayOfObjects.map((item: any, index) => (
              <JsonItemWithValidation
                key={index}
                item={item}
                validation={item.id ? validationsMap[item.id] : undefined}
                jsonLabels={jsonLabels}
              />
            ))
          )}
        </Box>
      );
    } else {
      // Single item display

      // For single items, we can accept either a direct ValidationResult or a Record
      // If it's a ValidationResult (has messages property), use it directly
      // Otherwise check if it's a Record with the item's ID
      const singleItemValidation =
        itemValidations && 'messages' in itemValidations
          ? (itemValidations as ValidationResult)
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (jsonObjectOrArrayOfObjects as any)?.id &&
            typeof itemValidations === 'object'
          ? (itemValidations as Record<string, ValidationResult>)[
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (jsonObjectOrArrayOfObjects as any).id
            ]
          : validationSummary;

      return (
        <Box>
          <JsonItemWithValidation
            item={jsonObjectOrArrayOfObjects}
            validation={singleItemValidation}
            jsonLabels={jsonLabels}
          />
        </Box>
      );
    }
  };

  return (
    <Box
      width={'100%'}
      sx={{
        backgroundColor: theme.vars.palette.background.default,
        borderRadius: 1,
        p: 3,
        pb: 1,
        mb: 3,
        boxShadow: 1,
        border: `1px solid ${theme.vars.palette.divider}`,
      }}
    >
      {/* Section header with validation summary */}
      <Stack
        sx={{
          width: '100%',
          mb: 1,
        }}
      >
        <Stack
          direction='row'
          spacing={1}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <Typography
              variant='body1'
              sx={{
                color: theme.vars.palette.text.primary,
                fontWeight: 'medium',
              }}
            >
              {`${sectionLabel}${name ? ':' : ''}`}
            </Typography>

            {name && (
              <>
                <Typography
                  variant='body1'
                  sx={{ color: theme.vars.palette.text.secondary }}
                >
                  {isLoading ? <Skeleton width={220} /> : name}
                </Typography>
                {!isLoading && <CopyButton textToCopy={name} />}
              </>
            )}
          </Stack>

          {/* Action buttons */}
          <Stack direction={'row'} spacing={1} sx={{ ml: 'auto', mb: 1 }}>
            <ValidationSummary counts={validationCounts} />
            {actionButtonOrElement}
          </Stack>
        </Stack>

        {/* ID section */}
        {showID && (
          <DebugInfoSlot
            label='ID'
            value={id}
            isLoading={isLoading}
            showCopyButton
          />
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Content section with skeletons when loading */}
      {isLoading ? (
        <Stack mb={2}>
          <Skeleton variant='rounded' height={23} />
        </Stack>
      ) : (
        renderContent()
      )}
    </Box>
  );
};

export default DebugInfoSection;
